/**
 * A que le mira el margen una regla.
 *
 * Salio de una prueba concreta: una cotizacion con un solo item al 1,9% de
 * margen se frenaba, y esa MISMA linea acompanada de otra al 60% pasaba sola,
 * porque el promedio de las dos daba 40%.
 */
export type AlcanceMargen = 'TOTAL' | 'POR_LINEA' | 'AMBOS';

export const ALCANCE_MARGEN: Record<AlcanceMargen, { label: string; ayuda: string }> = {
  TOTAL: {
    label: 'Solo el total',
    ayuda:
      'Mira el promedio de toda la cotización. Una línea regalada puede pasar escondida entre las demás.',
  },
  POR_LINEA: {
    label: 'Solo por línea',
    ayuda: 'Alcanza con que un item este por debajo del umbral.',
  },
  AMBOS: {
    label: 'El total y cada línea',
    ayuda: 'Frena si falla el promedio O si falla algun item. Es lo recomendado.',
  },
};
/**
 * Con qué mide el margen una regla (F6 E1). Markup es el de siempre; los otros dos se evalúan desde T16.
 */
export type MetricaDeMargen = 'MARKUP' | 'MARGEN_DESPUES_DE_PREVISIONES' | 'MARGEN_BRUTO';

/**
 * F6 T16: el evaluador ya mide con cada métrica (el servidor acepta las tres), así que la pantalla ofrece «Mide con».
 * E1 (T12 + T15 + T16) sale junta.
 */
export const SELECTOR_DE_METRICA_DISPONIBLE = true;

/**
 * Lote D (A5, decisión de Sebastián, 15-oct): por ahora las reglas miden con Markup. Las otras medidas se ven
 * deshabilitadas (una regla que ya mide con otra conserva la suya) y este texto lo explica ANTES de guardar.
 */
export const AVISO_MIDE_CON =
  'Por ahora las reglas miden con Markup. Cambiar la medida movería los tramos de margen; se habilita más adelante.';

export const METRICA_DE_MARGEN: Record<MetricaDeMargen, { label: string; ayuda: string }> = {
  MARKUP: {
    label: 'Markup',
    ayuda:
      'El de siempre: lo que se gana sobre el precio sin previsiones. Ejemplo: se vende 100, las previsiones son 7,5 y el costo 74: (100 − 7,5 − 74) ÷ (100 − 7,5) = 20 %.',
  },
  MARGEN_DESPUES_DE_PREVISIONES: {
    label: 'Margen después de previsiones',
    ayuda:
      'Lo que queda sobre el precio de venta después de las previsiones. Con el mismo ejemplo: (100 − 7,5 − 74) ÷ 100 = 18,5 %.',
  },
  MARGEN_BRUTO: {
    label: 'Margen bruto',
    ayuda:
      'Lo que queda sobre el precio de venta, sin contar las previsiones. Con el mismo ejemplo: (100 − 74) ÷ 100 = 26 %.',
  },
};

export type ApprovalMode = 'AUTO_APPROVE_IF_MARGINS_MET' | 'ALWAYS_REQUIRE_APPROVAL';
export type ApprovalRuleType =
  | 'AMOUNT_THRESHOLD'
  | 'GLOBAL_MARGIN'
  | 'LOB'
  | 'CUSTOMER_SEGMENT'
  | 'PRICING_MODEL'
  /** OBS-025: sólo en el formulario; al servidor viaja como `condicionPresupuesto: true`. */
  | 'PRESUPUESTO_CLIENTE';

export interface ApprovalRule {
  id: string;
  name: string;
  ruleType: ApprovalRuleType;
  isActive: boolean;
  priority: number;
  amountThreshold?: number | null;
  marginThreshold?: number | null;
  lobId?: string | null;
  lob?: { id: string; name: string } | null;
  customerSegment?: string | null;
  pricingModelId?: string | null;
  pricingModel?: { id: string; name: string } | null;
  requiredApprovers: number;
  alcanceMargen?: AlcanceMargen;
  /** Con qué mide el margen (sólo cuenta en empresas con el Pricer). */
  metrica?: MetricaDeMargen;
  pesoMinimoLineaPct?: number | null;
  /** OBS-025: aplica si el total del documento supera el presupuesto del cliente (sólo Pricer). */
  condicionPresupuesto?: boolean;
  approvers?: RuleApproverEntry[];
  createdAt: string;
}

/**
 * Un paso de la cadena de aprobación.
 *
 * Apunta a una persona **o** a un cargo, nunca a los dos. Antes solo existía la
 * opción de persona: la configuración decía "esto lo aprueba Delicia Rodríguez",
 * y el día que esa persona no estaba, la cotización quedaba trabada.
 */
export interface RuleApproverEntry {
  id: string;
  stepOrder: number;
  userId: string | null;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  customRoleId: string | null;
  customRole: {
    id: string;
    name: string;
  } | null;
}

/** Un cargo de la empresa, con cuánta gente lo tiene hoy. */
export interface CargoDisponible {
  id: string;
  name: string;
  description?: string | null;
  /**
   * Cuántas personas lo tienen. Importa mostrarlo: un cargo sin nadie asignado
   * traba la cadena, y es mejor avisarlo al configurar que descubrirlo cuando
   * una cotización se queda parada.
   */
  userCount: number;
  /** Si el cargo concede aprobar cotizaciones (el servidor rechaza guardar uno que no). */
  puedeAprobar?: boolean;
}

/** Lo que se manda al guardar: el orden del arreglo define la secuencia. */
export interface PasoAprobadorPedido {
  userId?: string;
  customRoleId?: string;
}

export interface ApproverUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  /** El cargo personalizado, si lo tiene (el que decide su permiso de aprobar). */
  roleName?: string | null;
}

export interface ApprovalWorkflowConfig {
  id: string | null;
  defaultMode: ApprovalMode;
  /**
   * Cuando dos reglas aplican a la vez, el sistema gobierna con una sola. Con
   * esto en true firman los aprobadores de todas las que aplicaron.
   */
  acumularAprobadores?: boolean;
  rules: ApprovalRule[];
}

export interface CreateRulePayload {
  name: string;
  ruleType: ApprovalRuleType;
  isActive?: boolean;
  priority?: number;
  amountThreshold?: number | null;
  marginThreshold?: number | null;
  lobId?: string | null;
  customerSegment?: string | null;
  pricingModelId?: string | null;
  requiredApprovers?: number;
  alcanceMargen?: AlcanceMargen;
  metrica?: MetricaDeMargen;
  pesoMinimoLineaPct?: number | null;
  condicionPresupuesto?: boolean;
}

/** Una persona de la empresa que puede ser observadora (no hace falta que apruebe). */
export interface WatcherUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleName?: string | null;
}

/** Un observador de una línea de producto: una persona o un cargo. */
export interface LobObserverRow {
  id: string;
  lobId: string;
  userId: string | null;
  customRoleId: string | null;
  user: { id: string; firstName: string; lastName: string; email: string } | null;
  customRole: { id: string; name: string } | null;
}
